/**
 * useRealtimePermissions - Real-time permission checking hook
 * 
 * This hook provides real-time permission checking by:
 * 1. Always fetching fresh permission data from API
 * 2. Using smart caching with TTL
 * 3. Auto-refreshing when permissions change
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/setup';
import { getUserCompaniesAndRoles } from '../../../modules/auth/redux/AuthCRUD';

import { applyUserCompaniesAndRoles } from '../utils/applyUserCompaniesAndRoles';

interface PermissionCache {
  data: any;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface RealtimePermissionContext {
  user: any;
  globalRoles: any[];
  selectedCompany: any;
  isLoading: boolean;
  lastUpdated: number;
}

export const useRealtimePermissions = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const globalRoles = useSelector((state: RootState) => state.auth.globalRoles);
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);

  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(0);
  const [permissionCache, setPermissionCache] = useState<PermissionCache | null>(null);
  const [, setLastDispatchTime] = useState(0);
  
  // Cache TTL: 1 hour (since we rely on browser refresh)
  const CACHE_TTL = 60 * 60 * 1000;
  
  // Check if cache is still valid
  const isCacheValid = useCallback(() => {
    if (!permissionCache) return false;
    const now = Date.now();
    return (now - permissionCache.timestamp) < permissionCache.ttl;
  }, [permissionCache]);
  
  // Fetch fresh permission data from API
  const fetchPermissions = useCallback(async (forceRefresh = false) => {
    if (isLoading) return;
    
    // Use cache if valid and not forcing refresh
    if (!forceRefresh && isCacheValid()) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      const response = await getUserCompaniesAndRoles();
      const parsed = applyUserCompaniesAndRoles(dispatch, response.data, {
        syncSelectedCompanyId: selectedCompany?.company_id,
      });

      const cacheData = {
        data: parsed,
        timestamp: Date.now(),
        ttl: CACHE_TTL,
      };
      setPermissionCache(cacheData);
      setLastUpdated(Date.now());
      setLastDispatchTime(Date.now());
      
      
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, isCacheValid, dispatch, selectedCompany]);
  
  // Initial fetch only - no time triggers
  useEffect(() => {
    // TEMPORARILY DISABLED TO DEBUG LAGGING ISSUE
    // if (!user) return;
    
    // // Only fetch if we don't have a selected company or if cache is invalid
    // if (!selectedCompany || !isCacheValid()) {
    //   fetchPermissions();
    // } 
  }, [user, selectedCompany, isCacheValid, fetchPermissions]);
  
  // Manual refresh function
  const refreshPermissions = useCallback(() => {
    fetchPermissions(true);
  }, [fetchPermissions]);
  
  // Create permission context
  const permissionContext: RealtimePermissionContext = useMemo(() => {
    return {
      user: user ? {
        ...user,
        id: String(user.id)
      } : { id: '', email: '', username: '' },
      globalRoles: globalRoles || [],
      selectedCompany: selectedCompany || null,
      isLoading,
      lastUpdated
    };
  }, [user, globalRoles, selectedCompany, isLoading, lastUpdated]);
  
  return {
    permissionContext,
    refreshPermissions,
    isLoading,
    lastUpdated
  };
};
